import React, { JSX, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { getData } from 'country-list';
import { BasicDropDown, BasicDropDownController } from '@components-clean/core/basic-dropdown';
import {
    ReviewFieldsType,
    RiskReviewFieldController,
} from '@components-clean/core/bulk-update-dialog/field-components/risk-review-field';
import {
    RiskStatusClosedDateFieldController,
    RiskStatusClosedDateFieldsType,
} from '@components-clean/core/bulk-update-dialog/field-components/risk-status-closed-date-field';
import Button from '@components-clean/core/button';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import {
    BulkUpdateDialogProps,
    DecisionDropDownOption,
    DynamicFieldProps,
    FieldDropDownListOption,
    OwnerDropDownOption,
    SelectedFieldValuesProps,
} from '@interfaces-clean/bulk-update.interface';
import { NamedItemType, OrganisationLovResponse } from '@interfaces-clean/organisationLov.interface';
import { mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { CountryInputType, NamedItemType as graphqlNamedItemType } from 'types/graphql-global-types';
import { useBoolean, useTranslation } from '@hooks/index';
import './bulk-update-dialog.scss';
import { RiskService } from '@utils/risk.service';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeVariables } from '@queries/generated/common.generated';
import {
    getStudyMembers,
    getStudyMembersVariables,
    getSmStudyMembers,
    getSmStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS, GET_SM_STUDY_MEMBERS } from '@queries/studies';
import OrganisationLovApi from '@apis/bff-api/central-monitoring/organisationLovs';

type MultiSelectIdItem = { id: string };

const COMPONENT_NAME = 'bulk-update-dialog';

const isMultiSelectIdItem = (value: unknown): value is MultiSelectIdItem => {
    return typeof value === 'object' && value !== null && 'id' in value;
};

type FormFieldValue =
    | string
    | Date
    | OwnerDropDownOption
    | DecisionDropDownOption
    | ReviewFieldsType
    | RiskStatusClosedDateFieldsType
    | CountryInputType[]
    | MultiSelectIdItem[]
    | null;

type DynamicFormData = {
    [key: `field-${number}`]: FormFieldValue;
};

type FieldDefinitionWithComponentProps = BulkUpdateDialogProps['fields'][number] & {
    componentProps?: {
        hideReviewRequired?: boolean;
    };
};

type LovOption = { ref: string; name: string; groupedBy: string; disabled?: boolean };

const BulkUpdateDialog = ({
    studyRef,
    onClose,
    onSubmit,
    dialogTitle,
    warningMessage,
    warningMessage2,
    fields,
    unsavedChangesArea,
    hasNewDatasetData,
}: BulkUpdateDialogProps) => {
    const { t } = useTranslation('common');

    const [addDisabled, setAddDisabled] = useState<boolean>(true);
    const [selectedFields, setSelectedFields] = useState<SelectedFieldValuesProps[]>(() => [
        {
            id: Date.now(),
            field: '',
            type: null,
            value: null,
            text: null,
            required: false,
            textLimit: 0,
            ignoreStudyRef: false,
        },
    ]);
    const fieldHoldersRef = useRef<Record<number, HTMLDivElement | undefined>>({});
    const lastFieldRef = useRef<HTMLDivElement | null>(null);
    const [decisionLov, setDecisionLov] = useState<OrganisationLovResponse[]>([]);
    const [controlsDecisionLov, setControlsDecisionLov] = useState<OrganisationLovResponse[]>([]);
    const {
        handleSubmit,
        formState: { isValid, isSubmitting },
        control,
        unregister,
    } = useForm<DynamicFormData>({ mode: 'onChange' });

    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
    const [isDirty, setIsDirty] = useState<boolean>(true);
    const [fieldWarningMessages, setFieldWarningMessages] = useState<Map<string, string>>(() => new Map());

    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string; disabled?: boolean } | null)[] | undefined
    >([]);

    const [smStudyMembers, setSmStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string; disabled?: boolean } | null)[] | undefined
    >([]);

    const [responsibleFunctions, setResponsibleFunctions] = useState<LovOption[]>([]);
    const [categories, setCategories] = useState<LovOption[]>([]);
    const [controlStrategies, setControlStrategies] = useState<LovOption[]>([]);
    const [ctQType, setCtQType] = useState<LovOption[]>([]);
    const [edcEntry, setEdcEntry] = useState<LovOption[]>([]);
    const [controlPhases, setControlPhases] = useState<LovOption[]>([]);
    const [controlTypes, setControlTypes] = useState<LovOption[]>([]);
    const [riskQualityIssues, setRiskQualityIssues] = useState<LovOption[]>([]);

    const booleanOptions: LovOption[] = [
        { ref: 'True', name: t('bulkUpdateDialog.true'), groupedBy: '' },
        { ref: 'False', name: t('bulkUpdateDialog.false'), groupedBy: '' },
    ];

    const [ctqTypeRequest, { data: ctqTypeData }] = useLazyQuery<getNamedItemsByType, getNamedItemsByTypeVariables>(
        GET_NAMED_ITEMS_BY_TYPE,
    );

    const [edcEntryRequest, { data: edcEntryData }] = useLazyQuery<getNamedItemsByType, getNamedItemsByTypeVariables>(
        GET_NAMED_ITEMS_BY_TYPE,
    );

    const [responsibleVendorRequest, { data: responsibleVendorData, loading: loadingResponsibleVendorData }] =
        useLazyQuery<getNamedItemsByType, getNamedItemsByTypeVariables>(GET_NAMED_ITEMS_BY_TYPE);

    const [loadStudyMembersData, { data: studyMembersData }] = useLazyQuery<getStudyMembers, getStudyMembersVariables>(
        GET_STUDY_MEMBERS,
    );

    const [loadSmStudyMembersData, { data: smStudyMembersData }] = useLazyQuery<
        getSmStudyMembers,
        getSmStudyMembersVariables
    >(GET_SM_STUDY_MEMBERS);

    const [responsibleFunctionRequest, { data: responsibleFunctionsData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const [controlStrategyRequest, { data: controlStrategyData }] =
        useLazyQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE);

    const [riskCategoryRequest, { data: riskCategoryData }] =
        useLazyQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE);

    const [riskQualityIssueRequest, { data: riskQualityIssueData }] =
        useLazyQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE);

    const [controlPhaseRequest, { data: controlPhaseData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const [controlTypeRequest, { data: controlTypeData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const { trigger: loadDecisionData, data: decisionLovData } = OrganisationLovApi.useLazyGet(
        studyRef ?? '',
        NamedItemType.ReviewLogDecision,
    );

    const { trigger: loadControlsDecisionData, data: controlsDecisionLovData } = OrganisationLovApi.useLazyGet(
        studyRef ?? '',
        NamedItemType.ControlsReviewLogDecision,
    );

    const isMutating = useBoolean(false);

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = [
                ...studyMembersData.studyMembers
                    .filter((user) => user && user.active)
                    .map((user) => ({
                        fullName: `${user?.firstName} ${user?.lastName}`,
                        ref: user?.id as string,
                        groupedBy: 'Active',
                    }))
                    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
                ...studyMembersData.studyMembers
                    .filter((user) => user && !user.active)
                    .map((user) => ({
                        fullName: `${user?.firstName} ${user?.lastName}`,
                        ref: user?.id as string,
                        groupedBy: 'Inactive',
                        disabled: true,
                    }))
                    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
            ];
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    useEffect(() => {
        if (smStudyMembersData?.smStudyMembers) {
            const smStudyUsers = [
                ...smStudyMembersData.smStudyMembers
                    .filter((user) => user && user.active)
                    .map((user) => ({
                        fullName: `${user?.firstName} ${user?.lastName}`,
                        ref: user?.id as string,
                        groupedBy: 'Active',
                    }))
                    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
                ...smStudyMembersData.smStudyMembers
                    .filter((user) => user && !user.active)
                    .map((user) => ({
                        fullName: `${user?.firstName} ${user?.lastName}`,
                        ref: user?.id as string,
                        groupedBy: 'Inactive',
                        disabled: true,
                    }))
                    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
            ];
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSmStudyMembers(smStudyUsers);
        }
    }, [smStudyMembersData?.smStudyMembers]);

    useEffect(() => {
        if (decisionLovData) {
            const decisionData = decisionLovData
                .filter((y) => y && y.name)
                .sort((a, b) => {
                    // Sort active items first (true values before false values)
                    return (b?.active ? 1 : 0) - (a?.active ? 1 : 0);
                })
                .map((data) => ({
                    name: data?.name ?? '',
                    id: data?.id ?? 0,
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: !data?.active,
                    active: data?.active,
                }));

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDecisionLov(decisionData);
        }
    }, [decisionLovData]);

    useEffect(() => {
        if (controlsDecisionLovData) {
            const controlsDecisionData = controlsDecisionLovData
                .filter((y) => y && y.name)
                .sort((a, b) => {
                    // Sort active items first (true values before false values)
                    return (b?.active ? 1 : 0) - (a?.active ? 1 : 0);
                })
                .map((data) => ({
                    name: data?.name ?? '',
                    id: data?.id ?? 0,
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: !data?.active,
                    active: data?.active,
                }));

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setControlsDecisionLov(controlsDecisionData);
        }
    }, [controlsDecisionLovData]);

    const sortedLovs = (data: getNamedItemsByType | null, type: string): LovOption[] => {
        if (!data?.namedItemsByType) return [];

        const ignoreStudyRef = selectedFields.find((field) => field.type === type)?.ignoreStudyRef;

        const active = data.namedItemsByType
            .filter((ni) => ni?.active)
            .flatMap((item) =>
                ((studyRef === 'null' || ignoreStudyRef) && item?.studyId === '000000000000000000000000') ||
                item?.studyId === studyRef
                    ? [
                          {
                              name: item?.name ?? '',
                              ref: (item?.id as string) ?? '',
                              groupedBy: 'Active',
                              disabled: false,
                          },
                      ]
                    : [],
            )
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        const inactive = data.namedItemsByType
            .filter((ni) => !ni?.active)
            .flatMap((item) =>
                ((studyRef === 'null' || ignoreStudyRef) && item?.studyId === '000000000000000000000000') ||
                item?.studyId === studyRef
                    ? [
                          {
                              name: item?.name ?? '',
                              ref: (item?.id as string) ?? '',
                              groupedBy: 'Inactive',
                              disabled: true,
                          },
                      ]
                    : [],
            )
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        return [...active, ...inactive];
    };

    useEffect(() => {
        if (responsibleFunctionsData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setResponsibleFunctions(sortedLovs(responsibleFunctionsData, 'responsibleFunctionLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [responsibleFunctionsData?.namedItemsByType]);

    useEffect(() => {
        if (ctqTypeData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setCtQType(sortedLovs(ctqTypeData, 'ctqTypeLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [ctqTypeData]);

    useEffect(() => {
        if (edcEntryData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setEdcEntry(sortedLovs(edcEntryData, 'edcEntryLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [edcEntryData]);

    useEffect(() => {
        if (riskCategoryData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setCategories(sortedLovs(riskCategoryData, 'categoryLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [riskCategoryData?.namedItemsByType]);

    useEffect(() => {
        if (riskQualityIssueData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setRiskQualityIssues(sortedLovs(riskQualityIssueData, 'riskQualityIssueLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [riskQualityIssueData?.namedItemsByType]);

    useEffect(() => {
        if (controlStrategyData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setControlStrategies(sortedLovs(controlStrategyData, 'controlStrategyLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [controlStrategyData?.namedItemsByType]);

    useEffect(() => {
        if (controlPhaseData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setControlPhases(sortedLovs(controlPhaseData, 'controlPhaseLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [controlPhaseData?.namedItemsByType, selectedFields]);

    useEffect(() => {
        if (controlTypeData?.namedItemsByType) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setControlTypes(sortedLovs(controlTypeData, 'controlTypeLov'));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [controlTypeData?.namedItemsByType, selectedFields]);

    const ownerPlaceHolder: OwnerDropDownOption = {
        fullName: '',
        ref: '',
        groupedBy: '',
        disabled: false,
    };

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    const TextField = ({ id, name, required, textLimit }: DynamicFieldProps) => (
        <div className="k-d-flex k-flex-auto k-justify-content-center">
            <TextAreaController
                key={name}
                className={styles.textAreaOverrides}
                control={control}
                defaultValue=""
                name={`field-${id}`}
                data-testid={`bulk-update-text-area-${name}`}
                fillMode="solid"
                maxLength={textLimit === 0 ? 3000 : textLimit}
                required={required}
                resizable="none"
                readOnly={isSubmitting}
            />
        </div>
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    const DateField = ({ id, name, required }: DynamicFieldProps) => (
        <DatePickerController
            key={name}
            control={control}
            name={`field-${id}`}
            data-testid={`bulk-update-date-picker-${name}`}
            fillMode="solid"
            required={required}
            readonly={isSubmitting}
        />
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    const OwnerField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={studyMembers ?? []}
            data-testid={`bulk-update-owner-${name}`}
            textField="fullName"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            defaultValue={ownerPlaceHolder}
            readOnly={isSubmitting}
        />
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    const SmOwnerField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={smStudyMembers ?? []}
            data-testid={`bulk-update-sm-owner-${name}`}
            textField="fullName"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            defaultValue={ownerPlaceHolder}
            readOnly={isSubmitting}
        />
    );

    const responsibleFunctionField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={responsibleFunctions ?? []}
            data-testid={`bulk-update-responsible-function-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const controlPhaseField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={controlPhases ?? []}
            data-testid={`bulk-update-control-phase-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const controlTypeField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={controlTypes ?? []}
            data-testid={`bulk-update-control-type-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const decisionField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={decisionLov ?? []}
            data-testid={`bulk-update-decision-${name}`}
            textField="name"
            dataItemKey="id"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const controlsDecisionField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={controlsDecisionLov ?? []}
            data-testid={`bulk-update-controls-decision-${name}`}
            textField="name"
            dataItemKey="id"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const booleanField = ({ id, name }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={booleanOptions ?? []}
            data-testid={`bulk-update-type-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required
            allowClear
            readOnly={isSubmitting}
        />
    );

    const ctqTypeField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={ctQType ?? []}
            data-testid={`bulk-update-type-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const edcEntryField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={edcEntry ?? []}
            data-testid={`bulk-update-edcEntry-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const responsibleVendorField = ({ id, name, required }: DynamicFieldProps) => (
        <MultiSelectController
            key={name}
            isEditing
            control={control}
            name={`field-${id}`}
            required={required}
            data={
                Array.isArray(responsibleVendorData?.namedItemsByType)
                    ? mapNamedItems(responsibleVendorData.namedItemsByType)
                    : []
            }
            loading={loadingResponsibleVendorData}
            disabled={loadingResponsibleVendorData || isSubmitting}
            data-testid={`bulk-update-responsibleVendors-${name}`}
            textField="name"
            dataItemKey="id"
            groupField="groupedBy"
            activeField="active"
            filterable
            allowCustom={false}
        />
    );

    const countryField = ({ id, name, required }: DynamicFieldProps) => (
        <MultiSelectController
            key={name}
            isEditing
            control={control}
            name={`field-${id}`}
            required={required}
            data={Object.assign(
                [],
                getData().map((countryObject: { code: string; name: string }) => ({
                    code: countryObject.code,
                    name: countryObject.name,
                })),
            )}
            data-testid={`bulk-update-countries-${name}`}
            textField="name"
            dataItemKey="code"
            filterable
            allowCustom={false}
            disabled={isSubmitting}
        />
    );

    const categoryField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={categories ?? []}
            data-testid={`bulk-update-category-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const riskQualityIssueField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={riskQualityIssues ?? []}
            data-testid={`bulk-update-risk-quality-issue-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const controlStrategyField = ({ id, name, required }: DynamicFieldProps) => (
        <BasicDropDownController
            key={name}
            control={control}
            name={`field-${id}`}
            data={controlStrategies ?? []}
            data-testid={`bulk-update-control-strategy-${name}`}
            textField="name"
            dataItemKey="ref"
            fillMode="solid"
            filterable
            required={required}
            groupField="groupedBy"
            allowClear
            readOnly={isSubmitting}
        />
    );

    const riskStatusClosedDateField = ({ id, name, required }: DynamicFieldProps) => (
        <RiskStatusClosedDateFieldController
            key={name}
            control={control}
            name={`field-${id}`}
            data-testid={`bulk-update-type-${name}`}
            required={required}
            validateRules={{
                validate: (value: RiskStatusClosedDateFieldsType | null) => {
                    if (!value?.riskStatus) return false;
                    if (value.riskStatus.id === 'CLOSED') return Boolean(value.closedDate);
                    return true;
                },
            }}
            onRiskStatusChange={(statusId) => {
                if (!name) return;

                const newMap = new Map(fieldWarningMessages);

                if (statusId === 'NOT_APPLICABLE') {
                    newMap.set(name, t('bulkUpdateDialog.warnings.riskStatus'));
                } else if (statusId === 'CLOSED') {
                    newMap.set(name, t('bulkUpdateDialog.warnings.riskStatusWillBeClosed'));
                } else if (statusId === 'OBSOLETE') {
                    newMap.set(name, t('bulkUpdateDialog.warnings.riskStatusWillBeObsolete'));
                } else {
                    newMap.delete(name);
                }

                setFieldWarningMessages(newMap);
            }}
            disabled={isSubmitting}
        />
    );

    const riskReviewField = ({ id, name, required }: DynamicFieldProps) => {
        const fieldDef = name
            ? (fields.find((f) => f.field === name) as FieldDefinitionWithComponentProps | undefined)
            : undefined;

        const hideReviewRequired = Boolean(fieldDef?.componentProps?.hideReviewRequired);

        return (
            <RiskReviewFieldController
                key={name}
                control={control}
                name={`field-${id}`}
                data-testid={`bulk-update-type-${name}`}
                required={required}
                hideReviewRequired={hideReviewRequired}
                validateRules={{
                    validate: (value: ReviewFieldsType | null) => {
                        if (!value) return false;

                        if (hideReviewRequired) {
                            return Boolean(
                                value.reviewFrequencyPeriod &&
                                value.reviewFrequencyPeriod >= RiskService.reviewFrequencyNumberMin &&
                                value.reviewFrequencyPeriod <= RiskService.reviewFrequencyNumberMax &&
                                value.reviewFrequencyUnit,
                            );
                        }

                        return Boolean(
                            (value.reviewRequired &&
                                value.reviewRequired.ref === 'True' &&
                                value.reviewFrequencyPeriod &&
                                value.reviewFrequencyPeriod >= RiskService.reviewFrequencyNumberMin &&
                                value.reviewFrequencyPeriod <= RiskService.reviewFrequencyNumberMax &&
                                value.reviewFrequencyUnit) ||
                            (value.reviewRequired && value.reviewRequired.ref === 'False'),
                        );
                    },
                }}
                handleInputChange={(value) => {
                    if (hideReviewRequired) return;
                    if (!name) return;

                    const newMap = new Map(fieldWarningMessages);

                    if (value.reviewRequired?.ref === 'False' && !newMap.has(name)) {
                        newMap.set(name, t('bulkUpdateDialog.warnings.reviewRequired'));
                        setFieldWarningMessages(newMap);
                    } else if (newMap.has(name)) {
                        newMap.delete(name);
                        setFieldWarningMessages(newMap);
                    }
                }}
                disabled={isSubmitting}
            />
        );
    };

    type FieldRenderer = (props: DynamicFieldProps) => JSX.Element;

    const fieldRendererMap: Record<string, FieldRenderer> = {
        text: TextField,
        date: DateField,
        ownerLov: OwnerField,
        smOwnerLov: SmOwnerField,
        responsibleFunctionLov: responsibleFunctionField,
        controlPhaseLov: controlPhaseField,
        controlTypeLov: controlTypeField,
        decisionLov: decisionField,
        controlsDecisionLov: controlsDecisionField,
        boolean: booleanField,
        ctqTypeLov: ctqTypeField,
        edcEntryLov: edcEntryField,
        responsibleVendorLov: responsibleVendorField,
        riskReviewFields: riskReviewField,
        countriesLov: countryField,
        categoryLov: categoryField,
        controlStrategyLov: controlStrategyField,
        riskStatusAndClosedDate: riskStatusClosedDateField,
        riskQualityIssueLov: riskQualityIssueField,
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            lastFieldRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
        }, 0);
    };

    const fieldData: FieldDropDownListOption[] = fields
        .filter((fieldObj) => !selectedFields.some((sel) => sel.field === fieldObj.field))
        .map((fieldObj) => ({ id: fieldObj.field, value: t(`bulkUpdateDialog.${fieldObj.field}`) }));

    const handleAddField = () => {
        setSelectedFields([
            ...selectedFields,
            {
                id: Date.now(),
                field: '',
                type: null,
                value: null,
                text: null,
                required: false,
                textLimit: 0,
                ignoreStudyRef: false,
            },
        ]);
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setAddDisabled(
            fieldData.length < 1 || selectedFields.some((field) => field.field === '' || field.field === undefined),
        );
        setSaveDisabled(selectedFields.some((field) => field.field === '' || field.field === undefined));
        setIsDirty(selectedFields.some((field) => field.field !== '' && field.field !== undefined));
        /* eslint-enable @eslint-react/set-state-in-effect */
        scrollToBottom();
    }, [fieldData, selectedFields]);

    const handleFieldChange = async (id: number, event: DropDownListChangeEvent) => {
        const selectedFieldId: string = (event.value as { id: string })?.id;
        const selectedType = fields.find((f) => f.field === selectedFieldId)?.type ?? null;
        const isRequired = fields.find((f) => f.field === selectedFieldId)?.required ?? null;
        const textLimit = fields.find((f) => f.field === selectedFieldId)?.textLimit ?? null;
        const ignoreStudyRef = fields.find((f) => f.field === selectedFieldId)?.ignoreStudyRef ?? undefined;

        if (studyRef && selectedType === 'ownerLov') {
            loadStudyMembersData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        }
        if (studyRef && selectedType === 'smOwnerLov') {
            loadSmStudyMembersData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        }
        if (studyRef && selectedType === 'responsibleFunctionLov') {
            responsibleFunctionRequest({ variables: { type: graphqlNamedItemType.RESPONSIBLE_FUNCTION } }).catch(
                (err) => window.console.error(err),
            );
        }

        if (selectedType === 'controlPhaseLov') {
            controlPhaseRequest({ variables: { type: graphqlNamedItemType.CONTROL_PHASE } }).catch((err) =>
                window.console.error(err),
            );
        }

        if (selectedType === 'controlTypeLov') {
            controlTypeRequest({ variables: { type: graphqlNamedItemType.CONTROL_TYPE } }).catch((err) =>
                window.console.error(err),
            );
        }

        if (studyRef && selectedType === 'decisionLov') {
            loadDecisionData().catch((err) => window.console.error(err));
        }
        if (studyRef && selectedType === 'controlsDecisionLov') {
            loadControlsDecisionData().catch((err) => window.console.error(err));
        }
        if (selectedType === 'ctqTypeLov') {
            ctqTypeRequest({ variables: { type: graphqlNamedItemType.CTQ_TYPE } }).catch((err) =>
                window.console.error(err),
            );
        }
        if (selectedType === 'edcEntryLov') {
            edcEntryRequest({ variables: { type: graphqlNamedItemType.CTQEDC_ENTRY } }).catch((err) =>
                window.console.error(err),
            );
        }
        if (selectedType === 'responsibleVendorLov') {
            responsibleVendorRequest({ variables: { type: graphqlNamedItemType.VENDOR } }).catch((err) =>
                window.console.error(err),
            );
        }
        if (studyRef && selectedType === 'categoryLov') {
            riskCategoryRequest({ variables: { type: graphqlNamedItemType.RISK_CATEGORY } }).catch((err) =>
                window.console.error(err),
            );
        }
        if (studyRef && selectedType === 'controlStrategyLov') {
            controlStrategyRequest({ variables: { type: graphqlNamedItemType.RISK_CONTROL_STRATEGY } }).catch((err) =>
                window.console.error(err),
            );
        }
        if (studyRef && selectedType === 'riskQualityIssueLov') {
            riskQualityIssueRequest({ variables: { type: graphqlNamedItemType.RISK_QUALITY_ISSUE } }).catch((err) =>
                window.console.error(err),
            );
        }

        unregister(`field-${id}`);
        const curField = selectedFields.find((field) => field.id === id);
        if (curField && curField.field !== selectedFieldId) {
            const newMap = new Map(fieldWarningMessages);
            newMap.delete(curField.field);
            setFieldWarningMessages(newMap);
        }
        if (selectedFieldId === 'reviewComplete') {
            if (hasNewDatasetData) {
                fieldWarningMessages.set(selectedFieldId, t('bulkUpdateDialog.warnings.hasNewDatasetData'));
                setFieldWarningMessages(fieldWarningMessages);
            }
        }

        if (selectedFieldId === undefined) {
            setSelectedFields((prevFields) => {
                const index = prevFields.findIndex((item) => item.id === id);
                if (index === -1) return prevFields;

                const newItem = {
                    id: Date.now(),
                    field: '',
                    type: null,
                    value: null,
                    text: null,
                    required: false,
                    textLimit: 0,
                    ignoreStudyRef: false,
                };

                return [...prevFields.slice(0, index), newItem, ...prevFields.slice(index + 1)];
            });
        } else {
            setSelectedFields((prevFields) =>
                prevFields.map((fieldObj) =>
                    fieldObj.id === id
                        ? {
                              ...fieldObj,
                              field: selectedFieldId,
                              type: selectedType,
                              value: null,
                              required: isRequired,
                              textLimit,
                              ignoreStudyRef,
                          }
                        : fieldObj,
                ),
            );
        }
        setSaveDisabled(false);
    };

    const handleDelete = (id: number) => {
        const curField = selectedFields.find((field) => field.id === id);
        if (curField) {
            const newMap = new Map(fieldWarningMessages);
            newMap.delete(curField.field);
            setFieldWarningMessages(newMap);
        }
        unregister(`field-${id}`);
        setSelectedFields((prevFields) => prevFields.filter((item) => item.id !== id));
        delete fieldHoldersRef.current[id];
    };

    const onSubmitHandler = async () =>
        handleSubmit(async (formData: DynamicFormData) => {
            const submittedValues: SelectedFieldValuesProps[] = selectedFields.map((field) => {
                const rawValue = formData[`field-${field.id}`];

                let value:
                    string | string[] | ReviewFieldsType | RiskStatusClosedDateFieldsType | CountryInputType[] | null;
                let text: string = '';

                switch (field.type) {
                    case 'date':
                        if (rawValue instanceof Date) value = rawValue.toISOString();
                        else if (typeof rawValue === 'string') value = rawValue;
                        else value = '';
                        break;

                    case 'ownerLov':
                    case 'smOwnerLov':
                        if (rawValue && typeof rawValue === 'object' && 'ref' in rawValue)
                            value = String(rawValue.ref ?? '');
                        else value = '';
                        break;

                    case 'decisionLov':
                    case 'controlsDecisionLov':
                        if (rawValue && typeof rawValue === 'object' && 'id' in rawValue && 'name' in rawValue) {
                            value = String((rawValue as { id?: unknown }).id ?? '');
                            text = String((rawValue as { name?: unknown }).name ?? '');
                        } else {
                            value = '';
                            text = '';
                        }
                        break;

                    case 'responsibleFunctionLov':
                    case 'ctqTypeLov':
                    case 'edcEntryLov':
                    case 'categoryLov':
                    case 'controlStrategyLov':
                    case 'boolean':
                    case 'controlPhaseLov':
                    case 'controlTypeLov':
                    case 'riskQualityIssueLov':
                        if (rawValue && typeof rawValue === 'object' && 'ref' in rawValue)
                            value = String(rawValue.ref ?? '');
                        else value = '';
                        break;

                    case 'countriesLov':
                        value = Array.isArray(rawValue) ? (rawValue as CountryInputType[]) : [];
                        break;

                    case 'riskReviewFields':
                        value = rawValue && typeof rawValue === 'object' ? (rawValue as ReviewFieldsType) : null;
                        break;

                    case 'riskStatusAndClosedDate':
                        value =
                            rawValue && typeof rawValue === 'object'
                                ? (rawValue as RiskStatusClosedDateFieldsType)
                                : null;
                        break;

                    case 'responsibleVendorLov':
                        value = Array.isArray(rawValue) ? rawValue.filter(isMultiSelectIdItem).map((v) => v.id) : [];
                        break;

                    default:
                        if (typeof rawValue === 'string') value = rawValue;
                        else value = '';
                }

                return {
                    id: field.id,
                    field: field.field,
                    type: field.type,
                    value,
                    text,
                    required: field.required,
                    textLimit: 0,
                };
            });

            await onSubmit(submittedValues);
        })();

    useUnsavedChangesTracking({
        area: unsavedChangesArea,
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: unsavedChangesArea,
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            className="bulk-update-window"
            dataTestId="edit-study-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={onSubmitHandler}
            primaryBtnLabel="Update"
            primaryBtnDisabled={saveDisabled || !isValid || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={665}
            initialWidth={767}
            bodyPadding="default"
        >
            <div className="vertical-top">
                {isMutating.value ? (
                    <Loading />
                ) : (
                    <>
                        <div className="k-d-grid">
                            <InfoField
                                message={
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div>{warningMessage}</div>
                                        {warningMessage2 && (
                                            <div style={{ whiteSpace: 'pre-line' }}>{warningMessage2}</div>
                                        )}
                                        {Array.from(fieldWarningMessages.entries()).map(([key, value]) => (
                                            <div key={key}>
                                                <span className="k-font-medium">
                                                    {t(`bulkUpdateDialog.${key}`)}&nbsp;-&nbsp;
                                                </span>
                                                {value}
                                            </div>
                                        ))}
                                    </div>
                                }
                                layout="stretch"
                                type="Warning"
                            />
                        </div>

                        <div className="k-w-full" id="bulk-update-container">
                            {selectedFields.map((field, index) => {
                                const renderField = fieldRendererMap[field.type ?? ''];
                                const isLastField = index === selectedFields.length - 1;

                                return (
                                    <div
                                        key={field.id}
                                        className="k-m-2"
                                        ref={isLastField ? lastFieldRef : null}
                                        style={{ borderBottom: '1px solid #3E536E1A', paddingBottom: '10px' }}
                                    >
                                        <div className="field-view k-gap-2">
                                            <div />
                                            <div>Field *</div>
                                            <div />
                                            <div>Value {field.required ? '*' : ''}</div>
                                        </div>

                                        <div className="field-view k-gap-2">
                                            <div className="bulk-update-delete-cell">
                                                {index > 0 && (
                                                    <FontAwesomeIcon
                                                        icon={['fas', 'trash-xmark']}
                                                        fontSize={16}
                                                        style={{ color: '#9E0000CC', cursor: 'pointer' }}
                                                        className="fa-lg"
                                                        onClick={() => handleDelete(field.id)}
                                                    />
                                                )}
                                            </div>

                                            <div
                                                className="k-d-flex k-pt-1"
                                                ref={(el) => {
                                                    if (el) fieldHoldersRef.current[field.id] = el;
                                                }}
                                            >
                                                <BasicDropDown
                                                    data-testid="bulk-update-dropdown"
                                                    onChange={(e) => handleFieldChange(field.id, e)}
                                                    textField="value"
                                                    dataItemKey="id"
                                                    data={fieldData}
                                                    filterable
                                                    allowClear
                                                    readOnly={isSubmitting}
                                                    value={
                                                        field.field === ''
                                                            ? null
                                                            : {
                                                                  value: t(`bulkUpdateDialog.${field.field}`),
                                                                  id: field.field,
                                                                  disabled: false,
                                                              }
                                                    }
                                                />
                                            </div>

                                            <div className="k-d-flex k-justify-content-center k-p-3 ">=</div>

                                            <div className="k-d-flex k-justify-content-center k-align-items-center">
                                                {renderField
                                                    ? renderField({
                                                          id: field.id,
                                                          name: field.field,
                                                          required: field.required || false,
                                                          textLimit: field.textLimit ?? 3000,
                                                      })
                                                    : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            <Button
                                data-testid="add_new_row_button"
                                disabled={!isValid || addDisabled || isSubmitting}
                                type="button"
                                primary
                                onClick={handleAddField}
                                className="k-m-2"
                            >
                                {t('bulkUpdateDialog.addNewRow')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </DraggableDialog>
    );
};

export default BulkUpdateDialog;
